import React from "react";
import { useNavigate } from "react-router-dom";
import useActivePersonnel from "../../../hooks/useActivePersonnel";
import useTableSort from "../../../hooks/useTableSort";
import SortableTableHeader from "../SortableTableHeader";

// Derive a human-readable relative time from an ISO-8601 timestamp.
// When lastUpdated is null (no active/found position), surface the locationStatus
// string directly so the user sees "No Active Position" / "No Locations Found".
const getRelativeTime = (lastUpdated, locationStatus) => {
  if (!lastUpdated) return locationStatus ?? "-";

  const diffMs = Date.now() - new Date(lastUpdated).getTime();
  const diffMins = Math.floor(diffMs / 60_000);

  if (diffMins < 1) return "just now";
  if (diffMins === 1) return "1 minute ago";
  if (diffMins < 60) return `${diffMins} minutes ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours === 1) return "1 hour ago";
  return `${diffHours} hours ago`;
};
const ActivePersonnel = () => {
  const navigate = useNavigate();
  const { personnel, loading, error } = useActivePersonnel();

  // Add table sorting
  const { sortedData, requestSort, getSortClass } = useTableSort(personnel, {
    initialKey: "employeeName",
    initialDirection: "asc",
  });

  const handleRowClick = (fullName) => {
    navigate(`/personnel-metrics?name=${encodeURIComponent(fullName)}`);
  };

  return (
    <>
      <p className="modal-description">
        Employees currently clocked into a job
      </p>

      <div className="modal-section modal-section--scroll">
        <h3>Currently Active</h3>

        {loading && <p className="modal-loading">Loading personnel data…</p>}

        {error && (
          <p className="modal-error">
            Unable to load personnel data. Please try again.
          </p>
        )}

        {!loading && !error && (
          <div className="modal-table-scroll">
            <table className="modal-table modal-table--clickable">
              <thead>
                <tr>
                  <SortableTableHeader
                    columnKey="employeeName"
                    onSort={requestSort}
                    sortClass={getSortClass("employeeName")}
                  >
                    Employee Name
                  </SortableTableHeader>
                  <SortableTableHeader
                    columnKey="currentJob"
                    onSort={requestSort}
                    sortClass={getSortClass("currentJob")}
                  >
                    Current Job
                  </SortableTableHeader>
                  <SortableTableHeader
                    columnKey="lastUpdated"
                    onSort={requestSort}
                    sortClass={getSortClass("lastUpdated")}
                  >
                    Last Updated Position
                  </SortableTableHeader>
                </tr>
              </thead>
              <tbody>
                {sortedData.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="modal-table-empty">
                      No active personnel found.
                    </td>
                  </tr>
                ) : (
                  sortedData.map((employee) => (
                    <tr
                      key={employee.employeeName}
                      onClick={() => handleRowClick(employee.employeeName)}
                      className="modal-table-row--clickable"
                      title="Click to view personnel details"
                    >
                      <td>{employee.employeeName}</td>
                      <td>{employee.currentJob ?? "-"}</td>
                      <td>
                        {getRelativeTime(
                          employee.lastUpdated,
                          employee.locationStatus,
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default ActivePersonnel;
